import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Images } from "lucide-react";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import PageHeader from "../../../../components/layout/page/PageHeader";

import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";

import BackButton from "../../../../components/buttons/back/BackButton";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";

import AlbumPhotoGrid from "../../components/AlbumPhotoGrid";
import AlbumGalleryModal from "../../components/AlbumGalleryModal";

import { useAlbumGallery } from "../hooks/useAlbumGallery";

import "./SharedAlbumPhotosPage.css";

export default function SharedAlbumPhotosPage() {
  const navigate = useNavigate();
  const { albumTag } = useParams();

  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const { album, images, loading, error, refresh } = useAlbumGallery(albumTag);

  const photoList = Array.isArray(images) ? images : [];

  const title = album?.title || "Album";
  const description = album?.description || "";

  const channelName = album?.brandName || album?.username || "Katch";

  const channelUrl = album?.username
    ? `/${encodeURIComponent(album.username)}`
    : null;

  const initials = String(channelName).trim().charAt(0).toUpperCase();

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load album photos."
    : "";

  const handleOpenGallery = (index) => {
    if (!photoList[index]) return;

    setSelectedPhoto(index);
  };

  const handleCloseGallery = () => {
    setSelectedPhoto(null);
  };

  const renderChannel = () => {
    if (!channelUrl) return null;

    return (
      <Link to={channelUrl} className="album-gallery-page__channel">
        {album?.avatarUrl ? (
          <img
            src={album.avatarUrl}
            alt=""
            className="album-gallery-page__channel-avatar"
          />
        ) : (
          <span className="album-gallery-page__channel-placeholder">
            {initials}
          </span>
        )}

        <span className="album-gallery-page__channel-info">
          <strong>{channelName}</strong>
          <small>@{album.username}</small>
        </span>
      </Link>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="album-gallery-page__loading" role="status">
          <Text>Loading photos...</Text>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="album-gallery-page__error" role="alert">
          <Text>{errorMessage}</Text>

          <Button value="Retry" variant="primary-outlined" onClick={refresh} />
        </div>
      );
    }

    if (!photoList.length) {
      return (
        <EmptyState
          icon={<Images size={28} />}
          title="No photos available"
          description="There are no photos in this album yet."
        />
      );
    }

    return (
      <AlbumPhotoGrid images={photoList} onPhotoClick={handleOpenGallery} />
    );
  };

  return (
    <PageWrapper>
      <div className="album-gallery-page">
        <div className="album-gallery-page__back">
          <BackButton onClick={() => navigate(-1)} />
        </div>

        <PageHeader>
          <div className="album-gallery-page__header">
            <div className="album-gallery-page__heading">
              <Heading>{title}</Heading>
            </div>

            {renderChannel()}
          </div>
        </PageHeader>

        <div className="album-gallery-page__content">{renderContent()}</div>

        {selectedPhoto !== null && !loading && !errorMessage && (
          <AlbumGalleryModal
            open
            images={photoList}
            initialIndex={selectedPhoto}
            title={title}
            description={description}
            brandName={album?.brandName}
            username={album?.username}
            avatarUrl={album?.avatarUrl}
            onClose={handleCloseGallery}
          />
        )}
      </div>
    </PageWrapper>
  );
}
